export type Condition = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | '>' | '>=' | '<' | '<=' | '=';


export type RTCReceiverParams = {
    ice?: string;
    media?: {
        video: boolean;
        audio: boolean;
    } | MediaStream | MediaStreamConstraints;
}

export type RTCConnectorParams = {
    cid: string;
    ice?: string;
    media?: {
        video: boolean;
        audio: boolean;
    } | MediaStream | MediaStreamConstraints;
    channels?: Array<RTCDataChannelInit | 'text-chat' | 'file-transfer' | 'video-chat' | 'voice-chat' | 'gaming'>;
}

export type RTCConnector = {
    hangup: () => void;
    connection: Promise<RTCResolved>;
}

export type RTCResolved = {
    target: RTCPeerConnection;
    channels: {
        [protocol: string]: RTCDataChannel
    };
    hangup: () => void;
    media: MediaStream;
}

export type RTCEvent = {
    type: 'track' | 'connectionstatechange' | 'close' | 'message' | 'open' | 'bufferedamountlow' | 'error' | 'icecandidate' | 'icecandidateend' | 'icegatheringstatechange' | 'negotiationneeded' | 'signalingstatechange';
    [key: string]: any;
}

export type WebSocketMessage = {
    type: 'message' | 'error' | 'success' | 'close' | 'notice' | 'private' | 'reconnect' | 'rtc:incoming' | 'rtc:closed';
    message?: any;
    connectRTC?: (params: RTCReceiverParams, callback: (e: RTCEvent) => void) => Promise<RTCResolved>;
    hangup?: () => void; // Reject incoming RTC connection.
    sender?: string; // user_id of the sender
    sender_cid?: string; // scid of the sender
    sender_rid?: string; // group of the sender
    code?: 'USER_LEFT' | 'USER_DISCONNECTED' | 'USER_JOINED' | null; // code for notice messeges
}

export type RealtimeCallback = (rt: WebSocketMessage) => void;

export type DelRecordQuery = GetRecordQuery & {
    unique_id?: string;
    record_id?: string;
};

export type GetRecordQuery = {
    unique_id?: string; // When unique_id is given, it will fetch the record with the given unique_id.
    record_id?: string; // When record_id is given, it will fetch the record with the given record_id. This overrides all other parameters.

    /** Table name not required when "record_id" is given. A bare string is shorthand for { name: <string> }. */
    table?: string | {
        /** Max 256 characters, where / ! * # % each count as 3. Blocks control chars and sentinel 􏿿. */
        name: string;
        /** Number range: 0 ~ 99. 'public' = 0, 'authorized' = 1, 'admin' = 99. '*' is shorthand for 'private'. Default: 'public' */
        access_group?: number | 'private' | '*' | 'public' | 'authorized' | 'admin';
        /** User ID of subscription */
        subscription?: string;
    };

    reference?: string | { record_id?: string; unique_id?: string; user_id?: string } // Referenced record ID or unique ID (string), or the object form. If user ID is given, it will fetch records that are uploaded by the user.

    /** Index condition and range cannot be used simultaneously.*/
    index?: {
        /** Custom names: max 256 characters, where / ! * # % each count as 3. Cannot start with "$". Blocks control chars and sentinel 􏿿. Reserved names: $uploaded, $updated, $referenced_count, $user_id. */
        name: string | '$updated' | '$uploaded' | '$referenced_count' | '$user_id';
        /** String value max 256 characters. Any punctuation is allowed and counts as one character, and values compare exactly as written. Blocks control chars and sentinel 􏿿. */
        value: string | number | boolean;
        /** For a string value: '>=' = 'starts with', '<=' = 'ends with'. When the name is a compound name ending in '.', '>=' / '<=' match the child name segment (starts / ends with). '>' / '<' are lexicographic; numbers/booleans compare normally. */
        condition?: Condition;
        range?: string | number | boolean;
    };
    tag?: string;
}

export type PostRecordConfig = {
    record_id?: string; // when record_id is given, it will update the record with the given record_id. If record_id is not given, it will create a new record. If unique ID is given as a record ID, it will update the record with the given unique ID. 
    unique_id?: string | null; // You can set unique_id to the record with the given unique_id. Null will remove unique_id from the record.
    readonly?: boolean; // When true, record cannot be updated or deleted.

    /** Table name not required when "record_id" is given.*/
    table?: {
        /** Max 256 characters, where / ! * # % each count as 3. Blocks control chars and sentinel 􏿿. */
        name?: string;
        /** Number range: 0 ~ 99. 'public' = 0, 'authorized' = 1, 'admin' = 99. '*' is shorthand for 'private'. Default: 'public' */
        access_group?: number | 'private' | '*' | 'public' | 'authorized' | 'admin';

        /** How the record reaches the uploader's subscribers. Signed-in users only. */
        subscription?: {
            is_subscription_record?: boolean; // When true, this record is a subscription record: the uploader's subscribers read it with getRecords() through table.subscription.
            upload_to_feed?: boolean; // When true, the record is added to the getFeed() of the uploader's subscribers who subscribed with get_feed.
            notify_subscribers?: boolean; // When true, creating the record sends a push notification to the uploader's subscribers who subscribed with get_notified. See "notification".
            feed_referencing_records?: boolean; // When true, records referencing this record are added to the getFeed() of this record's uploader.
            notify_referencing_records?: boolean; // When true, every new record referencing this one sends a push notification to this record's uploader's subscribers who subscribed with get_notified.
        } | null; // When null, it will remove all subscription settings from the record.
    };

    /**
     * Title and body of the push notification the record's creation sends to the uploader's
     * subscribers with table.subscription.notify_subscribers. Both are required, and together
     * they must fit 3072 bytes. Without it, subscribers get a default text. Ignored on updates
     * and without notify_subscribers.
     */
    notification?: {
        title: string;
        body: string;
    } | null;

    source?: {
        referencing_limit?: number; // Default: null (Infinite)
        prevent_multiple_referencing?: boolean; // If true, a single user can reference this record only once.
        can_remove_referencing_records?: boolean; // When true, owner of the record can remove any record that are referencing this record. Also when this record is deleted, all the record referencing this record will be deleted.
        only_granted_can_reference?: boolean; // When true, only the user who has granted private access to the record can reference this record.
        /** Index restrictions for referencing records. null removes all restrictions. */
        referencing_index_restrictions?: {
            /** Not allowed: White space, special characters. Allowed: Alphanumeric, Periods. */
            name: string; // Allowed index name
            /** Not allowed: Periods, special characters. Allowed: Alphanumeric, White space. */
            value?: string | number | boolean; // Allowed index value
            range?: string | number | boolean; // Allowed index range
            condition?: 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'ne' | '>' | '>=' | '<' | '<=' | '=' | '!='; // Allowed index value condition
        }[] | null;
        allow_granted_to_grant_others?: boolean; // When true, the user who has granted private access to the record can grant access to other users.
    };

    /** Can be record ID or unique ID */
    reference?: string | null; // When null, it will remove reference from the record.

    /** null removes index */
    index?: {
        /** Max 256 characters, where / ! * # % each count as 3. Cannot start with "$". Blocks control chars and sentinel 􏿿. */
        name: string;
        /** String value max 256 characters. Any punctuation is allowed and counts as one character, and values compare exactly as written. Blocks control chars and sentinel 􏿿. */
        value: string | number | boolean;
    } | null;

    tags?: string[] | null; // null removes all tags. each tag 1..256 characters, where / ! * # % each count as 3. Blocks control chars and sentinel 􏿿.
    remove_bin?: BinaryFile[] | string[] | null; // Removes bin data from the record. When null, it will remove all bin data.
    progress?: ProgressCallback; // Callback for database request progress. Useful when building progress bar.
    reference_private_key?: string; // When referencing a record that has private access, you can provide the private key of the referenced record to pass the access check. This is only required when the referenced record has private access and the user does not have access to the record through subscription or granted access.
}

export type BinaryFile = {
    access_group: number | 'private' | 'public' | 'authorized' | 'admin';
    filename: string;
    url: string;
    path: string;
    size: number;
    uploaded: number;
    getFile: (dataType?: 'base64' | 'download' | 'endpoint' | 'blob' | 'text' | 'info', progress?: ProgressCallback) => Promise<Blob | string | void | FileInfo>;
}

/**
 * Per-record encryption outcome. Present ONLY when the record's data went
 * through the client-side encryption layer, so its absence means the record was
 * stored in the clear.
 *
 * status 'encrypted' means the data in this object was decrypted successfully.
 * status 'failed' means `data` is null and `reason` says why:
 *   NO_SESSION_KEY      encryption is locked; call unlockEncryption()
 *   NOT_A_RECIPIENT     this user has no key wrap on the record
 *   BAD_KEY             the wrap did not open (wrong or rotated key)
 *   BINDING_MISMATCH    the envelope does not belong to this record
 *   CORRUPT             the payload failed its authentication tag
 *   UNSUPPORTED_VERSION written by a newer SDK
 *   ENCRYPTION_DISABLED the record is encrypted but this instance is not
 */
export type RecordEncryptionInfo = {
    status: 'encrypted' | 'failed';
    reason?: string;
    /** user_ids that hold a key wrap on this record. */
    recipients?: string[];
};

/** Options for `new Skapi(..., { encryption })`. */
export type EncryptionOptions = boolean | {
    /** PBKDF2 iteration count. Default 600000. Minimum 100000. */
    iterations?: number;
    /** 'tofu' pins a peer's key on first sight (default). 'strict' requires a prior pin. */
    trustPolicy?: 'tofu' | 'strict';
    /** Keep the master key in IndexedDB so a page reload stays unlocked. Default true. */
    persistDevice?: boolean;
    /** Refuse to enroll a password shorter than this. Default 0 (no check). */
    minPasswordLength?: number;
    /**
     * Issue a one-time recovery code at enrollment. Default 'code'.
     * 'none' opts out and accepts that a forgotten password means the user's
     * encrypted records are permanently unreadable.
     */
    recovery?: 'code' | 'none';
    /** Reserved keyring table name. Default '__skapi__keyring'. */
    table?: string;
};

export type RecordData = {
    record_id: string;
    unique_id?: string;
    user_id: string;
    updated: number;
    uploaded: number;
    referenced_count: number;
    /** Set only when the record's data passed through the encryption layer. */
    encrypted?: RecordEncryptionInfo;

    table: {
        name: string;
        /** Number range: 0 ~ 99 */
        access_group: number | 'private' | 'public' | 'authorized' | 'admin';
        /** Subscription settings of the record. See PostRecordConfig.table.subscription. */
        subscription?: {
            is_subscription_record: boolean; // When true, this record is a subscription record.
            upload_to_feed: boolean; // When true, the record is in the getFeed() of the uploader's subscribers who subscribed with get_feed.
            notify_subscribers: boolean; // When true, creating the record sent a push notification to the uploader's subscribers who subscribed with get_notified.
            feed_referencing_records: boolean; // When true, records referencing this record are added to the getFeed() of this record's uploader.
            notify_referencing_records: boolean; // When true, every new record referencing this one sends a push notification to the uploader's subscribers who subscribed with get_notified.
        };
    };
    source: {
        referencing_limit: number; // Default: null (Infinite)
        prevent_multiple_referencing: boolean; // If true, a single user can reference this record only once.
        can_remove_referencing_records: boolean; // When true, owner of the record can remove any record that are referencing this record. Also when this record is deleted, all the record referencing this record will be deleted.
        only_granted_can_reference: boolean; // When true, only the user who has granted private access to the record can reference this record.
        referencing_index_restrictions?: {
            name: string; // Allowed index name
            value?: string | number | boolean; // Allowed index value
            range?: string | number | boolean; // Allowed index range
            condition?: 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'ne' | '>' | '>=' | '<' | '<=' | '=' | '!='; // Allowed index value condition
        }[];
    };
    reference?: string; // record id of the referenced record.
    index?: {
        name: string;
        value: string | number | boolean;
    };
    data?: Record<string, any>;
    tags?: string[];
    bin: { [key: string]: BinaryFile[] };
    ip: string;
    readonly: boolean;
    /**
     * Present ONLY on an element of a postRecords() result that the backend refused.
     * Such an element is an empty record (record_id is ""), and this carries the reason
     * the backend gave, e.g. { code: 'NOT_EXISTS', message: 'Reference "..." does not exists.' }.
     * A saved record never has it, so `record_id` stays the test for "did this save".
     */
    error?: { code?: string; message?: string;[key: string]: any };
}

export type Connection = {
    /** User's locale */
    locale: string;
    user_agent: string;
    /** Connected user's IP address */
    ip: string;
    /** Service group */
    group: number;
    /** Service name */
    service_name: string;
    /** Service description */
    service_description: string;
    /** Service options */
    opt: {
        freeze_database: boolean;
        prevent_inquiry: boolean;
        prevent_signup: boolean;
        prevent_anonymous: boolean;
        /**
         * BunnyQuery: whether the embeddable chat requires an account before a
         * visitor can use it. Unrelated to `prevent_anonymous`, which governs
         * anonymous record WRITES. Defaults to true.
         */
        require_login?: boolean;
    },
    ai_agent?: string; // AI agent info.
}

export type Form<T> = HTMLFormElement | FormData | SubmitEvent | T;

export type Newsletter = {
    /** Newsletter id */
    message_id: string;
    /** Time sent out */
    timestamp: number;
    /** Number of complaints */
    complaint: number;
    /** Number of read */
    read: number;
    /** Subject */
    subject: string;
    /**
     * Number of bounced.<br>
     * When e-mail address is bounced, skapi no longer sends e-mail to the bounced address.
     */
    bounced: string;
    /**
     * Url of the message html.
     */
    url: string;
    /** Number users delivered */
    delivered: number;
    /**
     * Newsletter group the message was sent to.<br>
     * A number for the 0 ~ 99 groups, the group name for a named newsletter group.
     */
    group: number | string;
}

export type NewsletterGroup = {
    /** Name of the newsletter group. */
    group: string;
    /**
     * Access group required to subscribe to the group and to read its sent mail.<br>
     * 0 is anyone, 1 is any signed in user, 2 ~ 99 is that access group.
     */
    restriction: number;
    /** Display label of the group. Empty string when none was set. */
    name: string;
    /** Number of subscribers of the group. */
    subscribers: number;
    /**
     * E-Mail address a newsletter for this group is sent to.<br>
     * Empty string when the service has no sender e-mail set.
     */
    endpoint: string;
}

export type UserAttributes = {
    /** User's name */
    name?: string;
    /**
     * User's E-Mail for signin.<br>
     * 64 character max.<br>
     * When E-Mail is changed, E-Mail verified state will be changed to false.
     * E-Mail is only visible to others when set to public.
     * E-Mail should be verified to set to public.
     * */
    email?: string;
    /**
     * User's phone number. Format: "+0012341234"<br>
     * When phone number is changed, phone number verified state will be changed to false.
     * Phone number is only visible to others when set to public.
     * Phone number should be verified to set to public.
     */
    phone_number?: string;
    /** User's address, only visible to others when set to public. */
    address?: string | {
        /**
         * Full mailing address, formatted for display or use on a mailing label. This field MAY contain multiple lines, separated by newlines. Newlines can be represented either as a carriage return/line feed pair ("\r\n") or as a single line feed character ("\n").
         * street_address
         * Full street address component, which MAY include house number, street name, Post Office Box, and multi-line extended street address information. This field MAY contain multiple lines, separated by newlines. Newlines can be represented either as a carriage return/line feed pair ("\r\n") or as a single line feed character ("\n").
        */
        formatted: string;
        // City or locality component.
        locality: string;
        // State, province, prefecture, or region component.
        region: string;
        // Zip code or postal code component.
        postal_code: string;
        // Country name component.
        country: string;
    };
    /**
     * User's gender. Can be "female" and "male".
     * Other values may be used when neither of the defined values are applicable.
     * Only visible to others when set to public.
     */
    gender?: string;
    /** User's birthdate. String format: "1969-07-16", only visible to others when set to public.*/
    birthdate?: string;

    /** Additional string value that can be used freely. This is only accessible to the owner of the account and the admins. */
    misc?: string;
    picture?: string;
    profile?: string;
    website?: string;
    nickname?: string;

    /** User's E-Mail is public when true. E-Mail should be verified. */
    email_public?: boolean;
    /** User's phone number is public when true. Phone number should be verified. */
    phone_number_public?: boolean;
    /** User's address is public when true. */
    address_public?: boolean;
    /** User's gender is public when true. */
    gender_public?: boolean;
    /** User's birthdate is public when true. */
    birthdate_public?: boolean;
}

export type UserProfile = {
    /** Service id of the user account. */
    service: string;
    /** User ID of the service owner. */
    owner: string;
    /** Access level of the user's account. */
    access_group: number;
    /** User's ID. */
    user_id: string;
    /** Country code of where user first signed up from. */
    locale: string;
    /**
    Account approval info and timestamp.
    Comes with string with the following format: "{approver}:{approved | suspended}:{approved_timestamp}"
    
    {approver} is who approved the account:
        [by_master] is when account approval is done manually from skapi admin panel,
        [by_admin] is when approval is done by the admin account with api call within your service.
        [by_skapi] is when account approval is automatically done.
        Open ID logger ID will be the value if the user is logged with openIdLogin()
        This timestamp is generated when the user confirms their signup, or recovers their disabled account.
    
    {approved | suspended}
        [approved] is when the account is approved.
        [suspended] is when the account is blocked by the admin or the master.
    
    {approved_timestamp} is the timestamp when the account is approved or suspended.

     */
    approved: string;
    /** Last login timestamp(Seconds). */
    log: number;
    /** Shows true when user has verified their E-Mail. */
    email_verified?: boolean;
    /** Shows true when user has verified their phone number. */
    phone_number_verified?: boolean;
    /** User's E-Mail is public when true. E-Mail should be verified. */
    email_public?: boolean;
    /** User's phone number is public when true. Phone number should be verified. */
    phone_number_public?: boolean;
    /** User's address is public when true. */
    address_public?: boolean;
    /** User's gender is public when true. */
    gender_public?: boolean;
    /** User's birthdate is public when true. */
    birthdate_public?: boolean;

    /** User's name */
    name?: string;
    /**
     * User's E-Mail for signin.<br>
     * 64 character max.<br>
     * When E-Mail is changed, E-Mail verified state will be changed to false.
     * E-Mail is only visible to others when set to public.
     * E-Mail should be verified to set to public.
     * */
    email?: string;
    /**
     * User's phone number. Format: "+0012341234"<br>
     * When phone number is changed, phone number verified state will be changed to false.
     * Phone number is only visible to others when set to public.
     * Phone number should be verified to set to public.
     */
    phone_number?: string;
    /** User's address, only visible to others when set to public. */
    address?: string | {
        /**
         * Full mailing address, formatted for display or use on a mailing label. This field MAY contain multiple lines, separated by newlines. Newlines can be represented either as a carriage return/line feed pair ("\r\n") or as a single line feed character ("\n").
         * street_address
         * Full street address component, which MAY include house number, street name, Post Office Box, and multi-line extended street address information. This field MAY contain multiple lines, separated by newlines. Newlines can be represented either as a carriage return/line feed pair ("\r\n") or as a single line feed character ("\n").
        */
        formatted: string;
        // City or locality component.
        locality: string;
        // State, province, prefecture, or region component.
        region: string;
        // Zip code or postal code component.
        postal_code: string;
        // Country name component.
        country: string;
    };
    /**
     * User's gender. Can be "female" and "male".
     * Other values may be used when neither of the defined values are applicable.
     * Only visible to others when set to public.
     */
    gender?: string;
    /** User's birthdate. String format: "1969-07-16", only visible to others when set to public.*/
    birthdate?: string;

    /** Additional string value that can be used freely. This is only accessible to the owner of the account and the admins. */
    misc?: string;
    picture?: string;
    profile?: string;
    website?: string;
    nickname?: string;
};

export type UserPublic = {
    /** Access level of the user's account. */
    access_group: number;
    /** User's ID. */
    user_id: string;
    /** Country code of where user first signed up from. */
    locale: string;
    /**
    Account approval info and timestamp.
    Comes with string with the following format: "{approver}:{approved | suspended}:{approved_timestamp}"
    
    {approver} is who approved the account:
        [by_master] is when account approval is done manually from skapi admin panel,
        [by_admin] is when approval is done by the admin account with api call within your service.
        [by_skapi] is when account approval is automatically done.
        Open ID logger ID will be the value if the user is logged with openIdLogin()
        This timestamp is generated when the user confirms their signup, or recovers their disabled account.
    
    {approved | suspended}
        [approved] is when the account is approved.
        [suspended] is when the account is blocked by the admin or the master.
    
    {approved_timestamp} is the timestamp when the account is approved or suspended.

     */
    approved: string;
    /** Account created timestamp(13 digit milliseconds). */
    timestamp: number;
    /** Last login timestamp(Seconds). */
    log: number;
    /** Number of the user's subscribers. */
    subscribers: number;
    /** Number of subscription the user has made */
    subscribed: number;
    /** Number of the records the user have created. */
    records: number;

    /** User's name */
    name?: string;
    /**
     * User's E-Mail for signin.<br>
     * 64 character max.<br>
     * When E-Mail is changed, E-Mail verified state will be changed to false.
     * E-Mail is only visible to others when set to public.
     * E-Mail should be verified to set to public.
     * */
    email?: string;
    /**
     * User's phone number. Format: "+0012341234"<br>
     * When phone number is changed, phone number verified state will be changed to false.
     * Phone number is only visible to others when set to public.
     * Phone number should be verified to set to public.
     */
    phone_number?: string;
    /** User's address, only visible to others when set to public. */
    address?: string | {
        /**
         * Full mailing address, formatted for display or use on a mailing label. This field MAY contain multiple lines, separated by newlines. Newlines can be represented either as a carriage return/line feed pair ("\r\n") or as a single line feed character ("\n").
         * street_address
         * Full street address component, which MAY include house number, street name, Post Office Box, and multi-line extended street address information. This field MAY contain multiple lines, separated by newlines. Newlines can be represented either as a carriage return/line feed pair ("\r\n") or as a single line feed character ("\n").
        */
        formatted: string;
        // City or locality component.
        locality: string;
        // State, province, prefecture, or region component.
        region: string;
        // Zip code or postal code component.
        postal_code: string;
        // Country name component.
        country: string;
    };
    /**
     * User's gender. Can be "female" and "male".
     * Other values may be used when neither of the defined values are applicable.
     * Only visible to others when set to public.
     */
    gender?: string;
    /** User's birthdate. String format: "1969-07-16", only visible to others when set to public.*/
    birthdate?: string;

    picture?: string;
    profile?: string;
    website?: string;
    nickname?: string;
};

export type ProgressCallback = (e: {
    status: 'upload' | 'download';
    progress: number; // 0 ~ 100, number of percent completed.
    loaded: number; // Number of bytes loaded.
    total: number; // Total number of bytes to be loaded.
    currentFile?: File, // Only for uploadFiles()
    completed?: File[]; // Only for uploadFiles()
    failed?: File[]; // Only for uploadFiles()
    abort: () => void; // Aborts current data transfer. When abort is triggered during the FileList is on trasmit, it will continue to next file.
}) => void;

export type FetchOptions = {
    /** Maximum number of records to fetch per call */
    limit?: number;
    /** Fetch next batch of data. Will return empty list if there is nothing more to fetch. */
    fetchMore?: boolean;
    /** Result in ascending order if true, decending when false. */
    ascending?: boolean;
    /** Start key to be used to query from the certain batch of fetch. */
    startKey?: { [key: string]: any; };
    /** Callback for database request progress. Useful when building progress bar. */
    progress?: ProgressCallback;
}
export type RequestHistory = {
    id: string; // request id. Format: {stamp}:{entropy}
    status_code: number; // http status code of the request
    response_body: any;
    error?: any;
    created: number; // timestamp of when the request was created, in milliseconds. Set once and never changes.
    updated: number; // timestamp of the last update of the request status (e.g. when the response arrived), in milliseconds.
    executed?: number; // timestamp of when the worker actually BEGAN executing the request, in milliseconds. Distinct from `created`, which is when it was enqueued: a request can wait in the queue first, so `updated - executed` is the execution time while `updated - created` also includes the wait. Absent on a request that has not started yet, and on rows written before the worker recorded it.
    responded?: number; // timestamp of when the destination's response finished arriving, in milliseconds. Recorded once and never moved, unlike `updated`: finalizing a streamed request stores its response later and moves `updated` to that moment, so on a streamed request this is the only field that still says when the response came back. `responded - executed` is how long the call took. Absent on a request that has not settled, on one that failed before any response arrived, and on rows written before the worker recorded it.
    request_body: any;
    expires?: number; // timestamp of when the request history will be deleted in epoch time (seconds).
    status: 'pending' | 'running' | 'resolved' | 'failed';
    queue_name?: string; // queue name if the request is in queue, empty string if the request is not in queue.
    // Compact-listing stubs. Present ONLY when the history was fetched with `compact: true`,
    // in which case request_body/response_body are omitted (the full bodies never leave the
    // server); re-fetch without `compact`, or poll the item, when a full body is needed.
    request_text?: string; // stub: first text of the request's LAST user message, truncated. Missing when the request body's shape was unrecognisable.
    response_text?: string; // stub: the head of the response text, truncated.
    response_complete_marker?: boolean; // stub: whether the response carried the indexing completion marker.
    compact?: boolean; // true on items returned by a `compact: true` listing, so consumers know bodies were deliberately omitted rather than empty.
    poll?: (arg?: {
        latency?: number;
        onResponse?: (res:any, meta?: { executed?: number })=>void; // meta.executed is when the worker BEGAN executing the request, in milliseconds, when a running poll tick reported it. "res" is the destination's own answer and carries nothing of skapi's, so request-level facts arrive here instead. Absent for a request that began and ended between two ticks; the same value is on the history item as "executed".
        onError?: (err:any)=>void;
        onStream?: (chunk: string, seq: number)=>void;
    }) => Promise<any>; // function to poll the request status until it settles. The promise resolves with the final result of the request: the third-party API response body when it resolves, or the error payload when it fails. It does not resolve with a RequestHistory item, so "created" and "updated" are not on the polled value. A poll stopped by stopForwardRequestPolling() (or its deprecated alias stopClientSecretPolling()) resolves with { id, status: 'stopped' }. Optional argument "latency" can be used to set the latency of the polling in milliseconds. Default latency is 1000ms.
}

export type DatabaseResponse<T> = {
    list: T[];
    startKey: { [key: string]: any; } | 'end';
    endOfList: boolean;
    startKeyHistory: string[];
}

export type FileInfo = {
    url: string;
    filename: string;
    access_group: number | 'private' | 'public' | 'authorized';
    filesize: number;
    record_id: string;
    uploader: string;
    uploaded: number;
    fileKey: string;
}

export type ConnectionInfo = {
    /** Public project ID (service + owner composed into the two-segment token). Empty when the service has no uuid owner. */
    project_id: string;
    user_ip: string;
    user_agent: string;
    user_location: string;
    service_name: string;
    service_description: string;
    version: string;
    ai_agent: string;
    conf: {
        freeze_database: boolean;
        prevent_signup: boolean;
        prevent_inquiry: boolean;
        prevent_anonymous: boolean;
        /**
         * BunnyQuery: whether the embeddable chat requires an account before a
         * visitor can use it. Unrelated to `prevent_anonymous`, which governs
         * anonymous record WRITES. Defaults to true.
         */
        require_login?: boolean;
    }
};

export type Table = {
    table: string;
    number_of_records: string;
    size: number;
    number_of_records_in_access_group_public?: number;
    number_of_records_in_access_group_private?: number;
    number_of_records_in_access_group_authorized?: number;
    number_of_records_in_access_group_admin?: number;
    [number_of_records_in_access_group_xx: string]: number | string | undefined; // for other access groups
}

export type Index = {
    table: string;
    index: string;
    number_of_records: number;
    string_count: number;
    number_count: number;
    boolean_count: number;
    total_number: number;
    total_bool: number;
    average_number: number;
    average_bool: number;
}

export type Tag = {
    table: string;
    tag: string;
    number_of_records: number;
}

export type UniqueId = {
    unique_id: string;
    record_id: string;
}

export type Subscription = {
    subscriber: string; // User ID of the subscriber.
    subscription: string; // User ID of the user subscribed to.
    timestamp: number;
    blocked: boolean;
    get_feed: boolean; // Records the user posts with upload_to_feed appear in the subscriber's getFeed().
    get_notified: boolean; // The subscriber gets push notifications for records the user posts with notify_subscribers, and for new references to the user's records with notify_referencing_records. Needs subscribeNotification() on the subscriber's device.
    get_email: boolean;
}

/**
 * Comparison operator of a ticket condition row, and of `ip` and `user_agent`. The word forms
 * are normalized to the symbols on registration.
 *
 * - '=' and '!=' compare the way JavaScript's === does: true is not 1 and "1" is not 1.
 * - On two numbers, '>', '>=', '<' and '<=' compare numerically.
 * - On two strings, '>=' means "starts with" and '<=' means "ends with". '>' and '<' use plain string order.
 * - A string against a number (or the reverse), a boolean, null or undefined never passes '>', '>=', '<' or '<='.
 * - With a list value, '=' and the ordering operators pass when any member passes. '!=' passes when the value is none of them.
 */
export type TicketConditionOperator = '=' | '!=' | '>' | '>=' | '<' | '<=' | 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte';

/**
 * One row of a ticket condition list: `headers`, `data`, `params` or `user` of a ticket's
 * condition, or `headers`, `data` or `user` of a req action's response condition.
 *
 * How a list decides:
 * - Rows on the same key are alternatives: the key passes when any one of them matches.
 * - Every different key that has a match row (a row with an `operator`) must pass. The first key
 *   that does not fails the consumption with CONDITION_FAILED, `detail: { field, keys }`.
 * - A field missing from the request is a mismatch for its key, '!=' included, not an error.
 *   The only rows that pass on a missing field are `= undefined` and `!= null`.
 * - Every row is read, in order. For each key the FIRST matching row wins: its `setValueWhenMatch`
 *   applies and its `placeholder` captures, and later rows on that key are skipped. A row that
 *   does not match captures nothing, so several rows on one key, each with its own
 *   `setValueWhenMatch`, followed by a catch-all row, work as a lookup table.
 * - A capture-only row (a `placeholder` and no `operator`) never counts toward passing. It
 *   captures whenever its field exists.
 * - Header names ignore case, and header values are always strings. Query string values are
 *   JSON-parsed when they parse, otherwise they stay strings: `?qty=2` is the number 2 and
 *   `?code=LAUNCH24` the string "LAUNCH24". '=' is strict, so it compares the parsed value: a
 *   `params` row needs `value: 2`, not "2", to match `?qty=2`.
 *
 * null and undefined (`data` and `params` rows only, in a response condition too):
 * - `= null` passes when the field is present and null. `!= null` passes otherwise, a missing field included.
 * - `= undefined` passes when the field is missing. `!= undefined` passes when it is present, null included.
 * - '>', '>=', '<' and '<=' never pass with null or undefined.
 * - undefined is a row with an `operator` and no `value` key, which is what `value: undefined` sends.
 *   A value list may contain null. undefined is only ever a single value.
 */
export type TicketConditionRow = {
    /**
     * Where the row looks, relative to its own list and written without `${ }`:
     * - `data` rows: a path into the request body. "id" is the body's `id`, "order[id]" is `order.id`.
     * - `params` rows: a path into the query string, read the same way.
     * - `data` rows of a req action's response condition: a path into the response body ("status" is the response's `status`).
     * - `headers` rows: the header name, matched case-insensitively.
     * - `user` rows: the consumer attribute name.
     *
     * Never templated.
     */
    key: string;
    /** Absent on a capture-only row, which has a `placeholder` and no `value`. */
    operator?: TicketConditionOperator;
    /**
     * A literal, never templated. A list is read as described in TicketConditionOperator.
     * null, a list containing null, and undefined (an `operator` with no `value` key) are for
     * `data` and `params` rows only.
     */
    value?: string | number | boolean | null | undefined | Array<string | number | boolean | null>;
    /** `data` and `params` rows only. When this row is the first matching row of its key, the value at `key` in the request data is replaced by this before anything else reads it. null replaces nothing. */
    setValueWhenMatch?: any;
    /**
     * `data` and `params` rows only. Remembers the value at `key` under this name, and the
     * actions read it as `${placeholder[NAME]}`. A match row captures only when it is the first
     * matching row of its key. A field that is missing captures nothing. Must match
     * ^[A-Za-z_][A-Za-z0-9_]*$.
     */
    placeholder?: string;
}

/**
 * An HMAC signature over the request, computed with a secret shared with the sender. Verified
 * before anything else runs: the `timestamp` (when set) must be an integer within `tolerance` of
 * now, the bytes described by `signed` are signed with the stored secret (`secret_prefix` removed,
 * then decoded per `secret_encoding`), and the result is compared in constant time against every
 * `${signature}` captured from the header. Any failure is a plain mismatch.
 *
 * Templates (`signed`, `timestamp`) are literal text with these tokens: `${body}` (the raw request
 * body exactly as received), `${method}` (the HTTP method, upper case), `${header:Name}` (a request
 * header, case-insensitive; an absent header fails verification) and any capture from `parts` other
 * than `${signature}`. An unknown token is refused at registration. These tokens exist only in
 * the signature templates: they are not the references actions use (see TicketAction).
 *
 * Public-key signature schemes (RSA, ECDSA, Ed25519) are not supported.
 */
export type TicketSignatureCondition = {
    /**
     * The name of a Secret Key of the project, never the secret itself. Must exist at
     * registration. It is only used to verify the signature: no action sends it, and its
     * Destinations do not limit the ticket's req actions.
     */
    secretName: string;
    /** The request header carrying the signature. Case-insensitive. Up to 256 characters. */
    header: string;
    /** Default 'sha256'. */
    algorithm?: 'sha256' | 'sha1' | 'sha512';
    /** How the signature value in the header is encoded. Default 'hex'. */
    encoding?: 'hex' | 'base64';
    /** Splits the header value into items, each trimmed. 1 to 8 characters. Absent: the whole header value is one item. */
    separator?: string;
    /**
     * Patterns matched against each header item, in order; the first whose literal text fits
     * captures the rest. Each pattern is literal text with at most one `${name}` capture (letters,
     * digits and _; not `body` or `method`). `${signature}` may be captured by several items (any one matching passes) and
     * must appear in at least one pattern; any other name becomes a token for the templates (the
     * first capture wins). Items that match no pattern are ignored. Up to 10 patterns of up to 256
     * characters each. Default ["${signature}"].
     */
    parts?: string[];
    /** Template of the signed bytes. Up to 512 characters. Default "${body}". */
    signed?: string;
    /** Template resolving to unix time, in seconds or in milliseconds (a value above 10^12). Up to 512 characters. Absent: no timestamp check. */
    timestamp?: string;
    /** Seconds the timestamp may be away from now, 1 to 86400. Only used with `timestamp`. Default 300. */
    tolerance?: number;
    /** How the stored secret becomes the HMAC key bytes. Default 'raw'. */
    secret_encoding?: 'raw' | 'base64' | 'hex';
    /** Removed from the start of the stored secret before decoding. Up to 64 characters. */
    secret_prefix?: string;
}

/**
 * What a consumption request must look like before the ticket's actions run. The parts are
 * checked in the order listed here and the first part that fails is the reported one. An absent
 * or empty part checks nothing.
 */
export type TicketCondition = {
    /** Answer HTTP 200 even when the consumption fails. For webhooks that retry on errors. It only changes the status: a failing request still fails. */
    return200?: boolean;
    /** Absent = both allowed. */
    method?: 'GET' | 'POST';
    /** Verified first, over the raw request body. See TicketSignatureCondition. */
    signature?: TicketSignatureCondition;
    /**
     * The caller's IP address. Fails only when none of the listed values passes ('!=': when it
     * is one of them). A value that is empty ("" or []), null or missing checks nothing, and
     * registration drops it.
     */
    ip?: { operator: TicketConditionOperator; value: string | string[] };
    /** The User-Agent header, decided like `ip`. */
    user_agent?: { operator: TicketConditionOperator; value: string | string[] };
    /** Rows against the request headers. See TicketConditionRow. Every row compares with a value: null, or no `value`, is refused. */
    headers?: TicketConditionRow[];
    /** Rows against the request body. Refused when `method` is 'GET': a GET request has no body. */
    data?: TicketConditionRow[];
    /** Rows against the query string, read on GET and on POST. */
    params?: TicketConditionRow[];
    /**
     * Rows against the consumer's attributes (user_id, email, access_group, ...). Signed requests
     * only: an app user of this project calling consumeTicket() with `auth: true`. A third-party
     * webhook has no session, so a ticket using this always fails for webhooks (AUTH_REQUIRED).
     * The actions read the same attributes as `${user[key]}`. Every row compares with a value:
     * null, or no `value`, is refused.
     */
    user?: TicketConditionRow[];
    /**
     * Record ID the consumer must own or have been granted. Signed requests only, like `user`:
     * a ticket using this always fails for webhooks. The actions read it as `${record_access}`.
     */
    record_access?: string;
}

/**
 * What a `req` action checks on the response before its nested actions run: `headers` and
 * `data` rows work as in the ticket's condition (captures and `setValueWhenMatch` included).
 * `headers` rows read the response headers and `data` row keys are paths into the parsed
 * response body. `user` and `record_access` still check the consumer, so they only work for
 * signed requests. A response that is not 2xx fails before this is checked.
 */
export type TicketResponseCondition = Pick<TicketCondition, 'headers' | 'data' | 'user' | 'record_access'>;

/**
 * One step of a ticket's action chain. Actions run in order. When an action fails its `err`
 * chain runs and the consumption stops. Nothing is rolled back.
 *
 * `exe` values are templated right before the action runs. Every reference is written inside
 * `${ }`, and text outside `${ }` is always literal, whatever it looks like: "order[id]" is just
 * that text. A value that is exactly one `${...}` keeps the type of what it reads; inside longer
 * text it becomes text (JSON for anything but a string). `$${...}` writes a literal `${...}`.
 * Object keys, condition rows and `setValueWhenMatch` are never templated.
 *
 * | Reference | Reads |
 * |---|---|
 * | `${data}`, `${data[key]}` | The incoming request body (a text body too), or one key of it: `${data[id]}` is the body's `id`, `${data[order][id]}` its `order.id`. Always the incoming body, also in nested actions. |
 * | `${params}`, `${params[key]}` | The incoming query string, or one key of it. |
 * | `${headers[name]}` | An incoming header, name case-insensitive. Authorization and Cookie read "<redacted>". |
 * | `${placeholder[NAME]}` | A value a condition row captured with `placeholder`. |
 * | `${user}`, `${user[key]}` | The signed-in user's attributes. Signed requests only. |
 * | `${ip}`, `${user_agent}`, `${method}` | The caller's IP address, User-Agent and HTTP method. |
 * | `${record_access}` | The record ID the condition's `record_access` names. Signed requests only. |
 * | `${response}`, `${response[key]}` | The parsed body of the enclosing req action's response. Only in that req's nested `actions` and their `err` chains. |
 * | `${result}`, `${result[key]}` | The result of the previous action in the same chain. A nested chain and an `err` chain start without one. |
 * | `${error}`, `${error[key]}` | In an `err` chain, the failure: `code`, `message`, `detail`, `action` (the act) and `path`. |
 * | `${ticket}`, `${ticket[key]}` | This consumption: `id`, `service`, `owner`, `consume_id` and `timestamp`. |
 * | `${CLIENT_SECRET}` | Reserved. See `secretName` on the req action. |
 *
 * Registration refuses anything else inside `${ }` (an unknown root such as `${id}`, keys under
 * a root that takes none such as `${ip[x]}`, `${placeholder}` or `${headers}` without a key,
 * broken brackets) with a message listing these forms, and a reference written where it can
 * never resolve (`${response}` outside a req's nested actions, `${error}` outside an `err`
 * chain, `${record_access}` when the condition names no record). When the action runs, a
 * reference that does not resolve fails it before it does anything: PATH_NOT_FOUND,
 * PLACEHOLDER_MISSING for a placeholder that was never captured, or AUTH_REQUIRED for `${user}`
 * on a request that is not signed in. Its `err` chain runs and the consumption stops.
 */
export type TicketAction =
    | {
        /** Set the access group of a user. */
        act: 'acsg';
        exe: {
            /** 1 ~ 99, or "admin", or a reference that gives one when the action runs, such as "${placeholder[GROUP]}". */
            group: number | 'admin' | `${string}\${${string}}${string}`;
            /** Blank = the consumer (signed-in consumption only). The project owner cannot be a target. */
            user_id?: string;
        };
        err?: TicketAction[];
    }
    | {
        /** Grant private access to a record. */
        act: 'acsr';
        exe: {
            /** A record ID, not a unique ID. */
            record_id: string;
            /** Blank = the consumer. */
            user_id?: string | string[];
        };
        err?: TicketAction[];
    }
    | {
        /** Post a record. Everything but `user_id` is the payload postRecord() sends, so the same rules apply. A `unique_id` makes a retried webhook update the same record instead of adding one. */
        act: 'pstr';
        exe: {
            table: string | {
                name: string;
                access_group?: number | 'public' | 'authorized' | 'admin' | 'private';
                subscription?: NonNullable<PostRecordConfig['table']>['subscription'];
            };
            data?: any;
            index?: { name: string; value: string | number | boolean };
            tags?: string[];
            unique_id?: string;
            /** Update instead of create. */
            record_id?: string;
            reference?: string;
            readonly?: boolean;
            source?: PostRecordConfig['source'];
            /** Post as this user instead of the project owner. */
            user_id?: string;
        };
        err?: TicketAction[];
    }
    | {
        /** HTTP request with its own response condition and nested chain. Result: the parsed response body. */
        act: 'req';
        exe: {
            /**
             * http:// or https:// with a hostname, such as "https://api.example.com/orders/${data[id]}".
             * The scheme is always written out, so a URL that is one whole reference is refused.
             * No IP literal, no userinfo, never under the api domain. Redirects are not followed.
             * A value put into the URL with `${...}` is percent-encoded, so it cannot add path
             * segments or query parameters ("." and ".." are refused). `${CLIENT_SECRET}` is
             * never allowed here. Sent as a browser sends it: a hostname outside ASCII is
             * IDNA-encoded, a tab or line break is removed, and in the path and query a space,
             * any other control character and every character outside ASCII is percent-encoded.
             */
            url: string;
            /** Default GET. */
            method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
            /**
             * The name of a Secret Key of the project, never the secret itself. Must exist at
             * registration. Never templated.
             *
             * - `${CLIENT_SECRET}` in this action's `headers`, `data` and `params` values is
             *   replaced by the key's value, server side, when the call is sent. It is refused
             *   anywhere else: in the url, in other actions, in a req without `secretName`, in
             *   header names or object keys, and in condition rows. Text in the incoming request
             *   that reads "${CLIENT_SECRET}" is never expanded.
             * - The url's scheme and host must be written out: no "${...}" before the path.
             * - When the key has Destinations, the call must go to one of them, or it fails with
             *   REQUEST_FAILED, `detail: { reason: 'refused_address' }`. A req without
             *   `secretName` is not limited.
             * - The value is never logged. A copy in the answer or an error, as sent or escaped
             *   up to three times over (percent, backslash or HTML escapes; each level in one
             *   notation), reads "${CLIENT_SECRET}" instead. An answer of any size is read whole
             *   and still parses as JSON; reading a very long answer can run out of the
             *   ticket's time, which fails with REQUEST_FAILED, `detail: { reason: 'timeout' }`.
             *   A key that no longer exists fails with
             *   REQUEST_FAILED, `detail: { reason: 'secret_missing', secretName }`.
             */
            secretName?: string;
            /**
             * Request headers, values templated. Two headers are the engine's own, and
             * registration refuses either one, in any case and with any surrounding spaces:
             * Host (always the url's host) and X-Skapi-Ticket (every call carries
             * `X-Skapi-Ticket: <service id>/<ticket id>`).
             *
             * A name must be ASCII, with no ":", line break or NUL. A value cannot hold a line
             * break, NUL or a character outside Latin-1 (send such values in the body).
             * - Registration refuses a name that breaks this rule, and a value whose text outside
             *   its `${...}` references breaks it, so a header the call could never send is not
             *   saved.
             * - A value that breaks it only once templated fails the call with REQUEST_FAILED,
             *   `detail: { reason: 'invalid_header', header }`, before anything is sent.
             *
             * Each message names the header, never its value.
             */
            headers?: { [name: string]: string };
            /** POST and PUT body. Sent as JSON when a content-type header says application/json, else form encoded. */
            data?: any;
            /** Query string. */
            params?: { [key: string]: any };
            /** Legacy. Registration moves these rows to the end of `condition.data`. */
            match?: TicketConditionRow[];
            /** Checked against the response. Captures land in the shared placeholder pool. See TicketResponseCondition. */
            condition?: TicketResponseCondition;
            /**
             * Nested chain, run when the response passes `condition`. It reads the response body
             * as `${response}` and `${response[key]}`, while `${data}` is still the incoming
             * request body. Any action can nest, including another req with its own `secretName`.
             */
            actions?: TicketAction[];
        };
        err?: TicketAction[];
    };

/** An issued ticket as getTickets() and registerTicket() return it. */
export type Ticket = {
    ticket_id: string;
    description?: string;
    /** Remaining consumptions. Absent = unlimited. */
    count?: number;
    /** Absolute expiry in ms since epoch. Absent = never. */
    time_to_live?: number;
    /** true = once per user, n = n times. Absent or 0 = unlimited. Only enforced for signed-in consumers. */
    limit_per_user?: boolean | number;
    /** Created at (ms). */
    timestamp: number;
    /** Last registered at (ms). */
    updated?: number;
    condition?: TicketCondition;
    actions?: TicketAction[];
    /**
     * true on a ticket saved before this release (the dashboard marks it "previous rules"). It
     * keeps running by the rules it was saved with until it is registered again, which applies
     * the current rules. `condition` and `actions` are shown converted to the current format.
     * Returned to the project owner only. See
     * https://docs.skapi.com/deprecated/deprecated.html#tickets-saved-before-this-release
     */
    legacy?: boolean;
}

export type TicketErrorCode =
    | 'INVALID_SERVICE'
    | 'SERVICE_DISABLED'
    | 'TICKET_NOT_FOUND'
    | 'TICKET_EXPIRED'
    | 'TICKET_EXHAUSTED'
    | 'USER_LIMIT_REACHED'
    | 'ISSUER_CANNOT_CONSUME'
    | 'AUTH_REQUIRED'
    | 'METHOD_NOT_ALLOWED'
    | 'CONDITION_FAILED'
    | 'PATH_NOT_FOUND'
    | 'PLACEHOLDER_MISSING'
    | 'REQUEST_FAILED'
    | 'TIMEOUT'
    | 'ACTION_FAILED'
    | 'ACTION_FORBIDDEN'
    | 'INTERNAL_ERROR';

/**
 * The body a consume endpoint answers with when the consumption fails, and the `cause` of the
 * SkapiError consumeTicket() rejects with. A success body never has `stage`; an error body
 * always does, also when the ticket answers HTTP 200 (`return200`).
 */
export type TicketError = {
    code: TicketErrorCode;
    /** Human readable, one sentence. */
    message: string;
    stage: 'ticket' | 'condition' | 'action';
    /** Only when stage is "action": the action that failed and its place in the chain, such as "actions[1].err[0]". */
    action?: { act: TicketAction['act']; path: string };
    /**
     * Code specific, JSON safe. For example { expired_at } on TICKET_EXPIRED, and { field, keys }
     * on CONDITION_FAILED, where `field` is the part that failed ("data", "headers", ...) and
     * `keys` the row keys that did not pass. `field` is "loop" when the request was sent by a
     * ticket's own req action (a ticket cannot consume a ticket).
     *
     * REQUEST_FAILED carries { status, body } for an answer of 300 or above (`body` is the parsed
     * answer, or the first 4 KB of its text when longer, compact JSON for a JSON answer, with a
     * Secret Key the req sent replaced by the text "${CLIENT_SECRET}"), { reason } with reason
     * "timeout", "refused_address" or "connection", { reason: "invalid_header", header } for a
     * header that cannot be sent, or { reason: "secret_missing", secretName }.
     */
    detail?: { [key: string]: any };
    ticket_id: string;
}